// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updatePickUpWarehouse from "@insite/client-framework/Store/Context/Handlers/UpdatePickUpWarehouse";
import getRealTimeWarehouseInventory from "@insite/client-framework/Store/Data/RealTimeInventory/Handlers/GetRealTimeWarehouseInventory";
import translate from "@insite/client-framework/Translate";
import { BaseModel, WarehouseDto } from "@insite/client-framework/Types/ApiModels";
import { ButtonProps } from "@insite/mobius/Button";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderPresentationProps } from "@insite/mobius/DataTable/DataTableHeader";
import { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import {
    Button,
    DataTableBody,
    DataTableCell,
    DataTableHead,
    DataTableRow,
    GridContainer,
    GridItem,
    Icon,
    LoadingOverlay,
    Select,
} from "@insite/mobius/index";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import { SelectProps } from "@insite/mobius/Select";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@pace/App/Components/Notify";
import { XRealTimeInvResult } from "@pace/App/Handlers/RealTimeInventory/GetRealTimeWarehouseInventory";
import IconCheckmarkCircle from "@pace/XDL/Icons/IconCheckmarkCircle";
import IconXCircle from "@pace/XDL/Icons/IconXCircle";
import { safeJsonParse } from "@pace/XDL/Utilities";
import { useEffect, useMemo } from "react";

import React, { createRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: ApplicationState) => ({
    warehouseInfo: safeJsonParse(state.context.session.properties["headerWarehouseInformation"]) || {},
});

const mapDispatchToProps = {
    getRealTimeWarehouseInventory,
    updatePickUpWarehouse,
};

interface OwnProps {
    productId: string;
    unitOfMeasure: string;
    modalIsOpen: boolean;
    modalCloseHandler: () => void;
    extendedStyles?: ProductAvailabilityModalStyles;
}

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface ProductAvailabilityModalStyles {
    availabilityByWarehouseModal?: ModalPresentationProps;
    centeringWrapper?: InjectableCss;
    spinner?: InjectableCss;
    availabilityByWarehouseTable?: DataTableProps;
    availabilityByWarehouseTableHead?: DataTableHeadProps;
    availabilityByWarehouseTableHeader?: DataTableHeaderPresentationProps;
    availabilityByWarehouseTableBody?: DataTableBodyProps;
    availabilityByWarehouseTableRow?: DataTableRowProps;
    availabilityByWarehouseTableCell?: DataTableCellProps;
    availabilityByWarehouseNameCell?: DataTableCellProps;
    warehouseStateSelection?: SelectProps;
    currentWarehouseLabel?: TypographyPresentationProps;
    currentWarehouseValue?: TypographyPresentationProps;
    inventoryInfoContainer?: GridContainerProps;
    statusSelectionContainer?: GridItemProps;
    currentWarehouseInfoContainer?: InjectableCss;
    networkQtyContainer?: GridItemProps;
    networkQtyLabel?: TypographyPresentationProps;
    networkQtyValue?: TypographyPresentationProps;
    actionButton?: ButtonProps;
    tableContainer?: InjectableCss;
    scrollContainer?: InjectableCss;
}

export const productAvailabilityModalStyles: ProductAvailabilityModalStyles = {};

type XWarehouseDto = WarehouseDto & BaseModel;
type TStockFilter = "IN_STOCK" | "ALL";
const ProductAvailabilityModal = ({
    productId,
    unitOfMeasure,
    modalIsOpen,
    extendedStyles,
    warehouseInfo,
    modalCloseHandler,
    getRealTimeWarehouseInventory,
    updatePickUpWarehouse,
}: Props) => {
    const styles = useMemo(() => mergeToNew(productAvailabilityModalStyles, extendedStyles), []);
    const [isLoading, setIsLoading] = useState(false);
    const [warehouses, setWarehouses] = useState<XWarehouseDto[]>([]);
    const [stockInfo, setStockInfo] = useState<{ [key: string]: string }>({});
    const [stockStatus, setStockStatus] = useState<TStockFilter>("IN_STOCK");
    const [errorMessage, setErrorMessage] = useState("");
    const [nameSort, setNameSort] = useState<"ascending" | "descending" | "none">("none");
    const [distanceSort, setDistanceSort] = useState<"ascending" | "descending" | "none">("ascending");

    const toggleSort = (column: "name" | "distance") => {
        if (column === "name") {
            setDistanceSort("none");
            setNameSort(nameSort === "ascending" ? "descending" : "ascending");
        } else {
            setNameSort("none");
            setDistanceSort(distanceSort === "ascending" ? "descending" : "ascending");
        }
    };

    const parentBoxRef = createRef<HTMLDivElement>();
    const filteredWarehouses = useMemo(() => {
        const sortedWarehouses = (
            stockStatus === "ALL" ? warehouses : warehouses.filter(warehouse => warehouse.qty > 0)
        ).sort((w1, w2) => {
            let c1 = nameSort === "none" ? parseFloat(w1.properties?.distanceFromCurrentWarehouse) : w1.description;
            let c2 = nameSort === "none" ? parseFloat(w2.properties?.distanceFromCurrentWarehouse) : w2.description;
            // XNG-Changes: PAC-788 handle bad data
            if (distanceSort !== "none") {
                if (Number.isNaN(c1)) {
                    c1 = 999999;
                }
                if (Number.isNaN(c2)) {
                    c2 = 999999;
                }
            }

            if (c1 < c2) {
                return nameSort === "ascending" || distanceSort === "ascending" ? -1 : 1;
            }
            if (c1 > c2) {
                return nameSort === "ascending" || distanceSort === "ascending" ? 1 : -1;
            }
            return 0;
        });

        return sortedWarehouses;
    }, [warehouses, stockStatus, nameSort, distanceSort]);

    const loadAvailability = () => {
        setIsLoading(true);
        getRealTimeWarehouseInventory({
            productId,
            unitOfMeasure,
            expand: ["warehouses"],
            onComplete(result: XRealTimeInvResult) {
                setErrorMessage(result.errorMessage);
                setWarehouses((result.warehouses || []) as XWarehouseDto[]);
                setStockInfo(result.additionalResult || {});
                setIsLoading(false);
            },
        });
    };

    const cleanupAvailability = () => {
        setWarehouses([]);
        setStockStatus("IN_STOCK");
        setErrorMessage("");
        setIsLoading(false);
        setStockInfo({});
        setNameSort("none");
        setDistanceSort("ascending");
    };

    const assignAlternateWarehouse = (warehouseId?: string) => {
        if (!warehouseId) {
            return;
        }
        setIsLoading(true);
        /*
            As decided we just can change pickup address
        */
        updatePickUpWarehouse({
            pickUpWarehouse: {
                id: warehouseId,
            } as any,
            onComplete: () => setIsLoading(false),
        });
    };

    useEffect(() => {
        if (modalIsOpen) {
            loadAvailability();
        } else {
            cleanupAvailability();
        }
        // In case we change current warehouse refetch data
    }, [modalIsOpen, warehouseInfo?.Name]);

    return (
        <Modal
            {...styles.availabilityByWarehouseModal}
            headline={translate("Product Availability")}
            isOpen={modalIsOpen}
            handleClose={modalCloseHandler}
        >
            <LoadingOverlay loading={isLoading} id="product-availability-loading-container">
                {!!Object.keys(warehouseInfo).length && (
                    <StyledWrapper {...styles.currentWarehouseInfoContainer}>
                        <Typography {...styles.currentWarehouseLabel}>{translate("Current Warehouse")}:</Typography>
                        <Typography {...styles.currentWarehouseValue}>
                            {warehouseInfo.Description} - {warehouseInfo.AddressToDisplay}
                        </Typography>
                    </StyledWrapper>
                )}
                <GridContainer {...styles.inventoryInfoContainer}>
                    <GridItem {...styles.statusSelectionContainer}>
                        <Select
                            {...styles.warehouseStateSelection}
                            label={translate("Stock Status")}
                            value={stockStatus}
                            onChange={event => setStockStatus(event.target.value as TStockFilter)}
                            data-test-selector="ProductAvailability_stock-status-selection"
                            sizeVariant="small"
                            labelPosition="left"
                        >
                            <option value="ALL">{translate("All Warehouses")}</option>
                            <option value="IN_STOCK">{translate("In Stock Warehouses")}</option>
                        </Select>
                    </GridItem>
                    {stockInfo && (
                        <GridItem {...styles.networkQtyContainer}>
                            {Number(stockInfo.qtyInNetwork || "0") > 0 ? (
                                <Icon width={12} height={13} src={IconCheckmarkCircle} color="success" />
                            ) : (
                                <Icon width={12} height={13} src={IconXCircle} color="danger" />
                            )}
                            <Typography {...styles.networkQtyLabel}>{translate("In Network:")}</Typography>
                            <Typography {...styles.networkQtyValue}>{stockInfo.qtyInNetwork || 0}</Typography>
                        </GridItem>
                    )}
                </GridContainer>
                {!isLoading && (
                    <>
                        {errorMessage ? (
                            <Typography>{errorMessage}</Typography>
                        ) : (
                            <StyledWrapper {...styles.tableContainer} ref={parentBoxRef}>
                                <DataTable
                                    {...styles.availabilityByWarehouseTable}
                                    data-test-selector="availabilityByWarehouseModal"
                                >
                                    <DataTableHead {...styles.availabilityByWarehouseTableHead}>
                                        <DataTableHeader
                                            sorted={nameSort}
                                            onSortClick={() => toggleSort("name")}
                                            {...styles.availabilityByWarehouseTableHeader}
                                        >
                                            {translate("Warehouse Name")}
                                        </DataTableHeader>
                                        <DataTableHeader {...styles.availabilityByWarehouseTableHeader}>
                                            {translate("Inventory")}
                                        </DataTableHeader>
                                        <DataTableHeader
                                            sorted={distanceSort}
                                            onSortClick={() => toggleSort("distance")}
                                            {...styles.availabilityByWarehouseTableHeader}
                                        >
                                            {translate("Distance To Current Warehouse")}
                                        </DataTableHeader>
                                        <DataTableHeader {...styles.availabilityByWarehouseTableHeader}>
                                            {translate("Action")}
                                        </DataTableHeader>
                                    </DataTableHead>
                                    <DataTableBody {...styles.availabilityByWarehouseTableBody}>
                                        {filteredWarehouses?.map(warehouse => (
                                            <DataTableRow
                                                {...styles.availabilityByWarehouseTableRow}
                                                key={warehouse.name}
                                            >
                                                <DataTableCell
                                                    {...styles.availabilityByWarehouseNameCell}
                                                    data-test-selector="warehouseAvailabilityModal_Description"
                                                >
                                                    {`${warehouse.name} - ${warehouse.description} - ${
                                                        warehouse.properties.city || ""
                                                    }${
                                                        warehouse.properties.state
                                                            ? `, ${warehouse.properties.state}`
                                                            : ""
                                                    }`}
                                                </DataTableCell>
                                                <DataTableCell
                                                    {...styles.availabilityByWarehouseTableCell}
                                                    data-test-selector="warehouseAvailabilityModal_Quantity"
                                                >
                                                    {warehouse.qty}
                                                </DataTableCell>
                                                <DataTableCell
                                                    {...styles.availabilityByWarehouseTableCell}
                                                    data-test-selector="warehouseAvailabilityModal_Distance"
                                                >
                                                    {warehouse.properties?.distanceFromCurrentWarehouse || "0.00 mi"}
                                                </DataTableCell>
                                                <DataTableCell
                                                    {...styles.availabilityByWarehouseTableCell}
                                                    data-test-selector="warehouseAvailabilityModal_Action"
                                                >
                                                    <Button
                                                        {...styles.actionButton}
                                                        onClick={() =>
                                                            assignAlternateWarehouse(warehouse.properties?.warehouseId)
                                                        }
                                                    >
                                                        {translate("Select Alternate Warehouse")}
                                                    </Button>
                                                </DataTableCell>
                                            </DataTableRow>
                                        ))}
                                        {!filteredWarehouses?.length && (
                                            <DataTableRow>
                                                <DataTableCell colSpan={4}>
                                                    <Notify type="info" showClose={false}>
                                                        {siteMessage("PACE_NOT_AVAILABLE_IN_WAREHOUSES")}
                                                    </Notify>
                                                </DataTableCell>
                                            </DataTableRow>
                                        )}
                                    </DataTableBody>
                                </DataTable>
                            </StyledWrapper>
                        )}
                    </>
                )}
            </LoadingOverlay>
        </Modal>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductAvailabilityModal);
